/**
 * TaskTimerCell - Célula de cronômetro de trabalho ativo na listagem de tarefas
 * 
 * Exibe tempo de trabalho ativo (excluindo pausas) com botões inline:
 * - Pending: 00:00 + botão Iniciar (play verde)
 * - In Progress: timer contando ao vivo + botão Pausar (amarelo)
 * - Paused: timer congelado + badge PAUSADO + botão Retomar (play verde)
 * - Completed/Cancelled: timer final congelado, sem botões
 * 
 * Fórmula: Tempo ativo = (now - started_at) - total_pause_duration_seconds
 * 
 * @version 1.5.0
 * RELEASE: 2026-10-04 v1.5.0 - Permite delegar a pausa para o diálogo opcional de motivo.
 * RELEASE: 2026-10-04 v1.4.0 - Reutiliza a regra central do cronômetro de tarefas.
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial
 * RELEASE: 2026-05-07 v1.1.0 - Congela cronômetro usando completed_at/cancelled_at
 * RELEASE: 2026-05-07 v1.2.0 - Sem prazo (due_date null) zerava o cronômetro
 * RELEASE: 2026-05-07 v1.3.0 - Cronômetro depende apenas de started_at; due_date
 *   não é mais bloqueio (corrige tarefas iniciadas sem prazo que ficavam em 00:00)
 */

import { useState, useEffect, useCallback } from 'react';
import { Play, Pause, PauseCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { calculateTaskActiveSeconds, formatTaskTimer } from '@/lib/tasks/taskTimer';

/** Logger do módulo */
const logger = createLogger('TaskTimerCell');

/** Props do componente */
interface TaskTimerCellProps {
  /** ID da tarefa */
  taskId: string;
  /** Status atual da tarefa */
  status: string;
  /** Data/hora de início da tarefa (null se pending) */
  startedAt: string | null;
  /** Se está pausada atualmente */
  isPaused: boolean;
  /** Tempo total de pausa em segundos (pausas finalizadas) */
  totalPauseDurationSeconds: number;
  /** Início da pausa ativa (null se não pausada) */
  currentPauseStartedAt: string | null;
  /** Se o usuário pode alterar status */
  canChangeStatus?: boolean;
  /** Solicita a pausa externamente, permitindo abrir o diálogo opcional de motivo. */
  onPauseRequest?: () => void;
  /** v1.1.0: Data/hora de conclusão — congela o cronômetro quando preenchida */
  completedAt?: string | null;
  /** v1.1.0: Data/hora de cancelamento — congela o cronômetro quando preenchida */
  cancelledAt?: string | null;
  /**
   * v1.2.0/v1.3.0: Prazo da tarefa (due_date).
   * Mantido apenas por compatibilidade com chamadas existentes — o cronômetro
   * agora depende exclusivamente de started_at e do status. Tarefas sem prazo
   * mas iniciadas devem contar normalmente.
   */
  dueDate?: string | null;
}

export function TaskTimerCell({
  taskId,
  status,
  startedAt,
  isPaused,
  totalPauseDurationSeconds,
  currentPauseStartedAt,
  canChangeStatus = false,
  onPauseRequest,
  completedAt = null,
  cancelledAt = null,
  dueDate = null,
}: TaskTimerCellProps) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());

  // v1.3.0: O cronômetro depende apenas de started_at + status.
  // due_date deixou de bloquear a contagem (uma tarefa pode ser iniciada sem prazo).
  void dueDate; // mantido por compatibilidade da assinatura

  // Timer ao vivo: atualiza a cada segundo apenas quando in_progress (não pausada,
  // não concluída/cancelada e com início registrado).
  useEffect(() => {
    if (!startedAt) return;
    if (status !== 'in_progress' || isPaused || completedAt || cancelledAt) return;

    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [status, isPaused, completedAt, cancelledAt, startedAt]);

  // Calcula tempo ativo (referência inteligente).
  // Sem started_at => 0 (tarefa nunca iniciada).
  const activeSeconds = calculateTaskActiveSeconds(
    startedAt,
    isPaused,
    totalPauseDurationSeconds,
    currentPauseStartedAt,
    completedAt,
    cancelledAt
  );

  /** Invalidar caches relacionados */
  const invalidateRelated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tasks-paginated'] });
    queryClient.invalidateQueries({ queryKey: ['task-details', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-pauses', taskId] });
    queryClient.invalidateQueries({ queryKey: ['task-kanban-column'] });
    queryClient.invalidateQueries({ queryKey: ['task-kanban-counts'] });
    queryClient.invalidateQueries({ queryKey: ['task-list-kpis'] });
  }, [queryClient, taskId]);

  /** Iniciar tarefa (pending → in_progress) */
  const handleStart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      const { error } = await supabase.rpc('change_task_status', {
        p_task_id: taskId,
        p_new_status: 'in_progress' as any,
      });
      if (error) throw error;
      toast.success('Tarefa iniciada');
      invalidateRelated();
    } catch (err: any) {
      logger.error('Erro ao iniciar tarefa', { error: err.message });
      toast.error(err.message || 'Erro ao iniciar tarefa');
    } finally {
      setLoading(false);
    }
  };

  /** Pausar tarefa (in_progress → paused) */
  const handlePause = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;

    // O painel rápido delega a pausa para o diálogo oficial de motivo.
    if (onPauseRequest) {
      onPauseRequest();
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.rpc('pause_task', {
        p_task_id: taskId,
        p_reason: null,
        p_tenant_hint: null,
      });
      if (error) throw error;
      toast.success('Tarefa pausada');
      invalidateRelated();
    } catch (err: any) {
      logger.error('Erro ao pausar tarefa', { error: err.message });
      toast.error(err.message || 'Erro ao pausar tarefa');
    } finally {
      setLoading(false);
    }
  };

  /** Retomar tarefa (paused → in_progress) */
  const handleResume = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setLoading(true);
    try {
      const { error } = await supabase.rpc('resume_task', {
        p_task_id: taskId,
        p_tenant_hint: null,
      });
      if (error) throw error;
      toast.success('Tarefa retomada');
      invalidateRelated();
    } catch (err: any) {
      logger.error('Erro ao retomar tarefa', { error: err.message });
      toast.error(err.message || 'Erro ao retomar tarefa');
    } finally {
      setLoading(false);
    }
  };

  // =========================================================================
  // Renderização por status
  // =========================================================================

  /** Status pending: 00:00 cinza + botão play verde */
  if (status === 'pending') {
    return (
      <div className="flex items-center justify-center gap-1.5">
        <span className="text-[11px] font-mono text-muted-foreground/50">00:00</span>
        {canChangeStatus && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
            onClick={handleStart}
            disabled={loading}
            title="Iniciar tarefa"
          >
            <Play className="h-3.5 w-3.5" fill="currentColor" />
          </Button>
        )}
      </div>
    );
  }

  /** Status in_progress: timer ao vivo + botão pause amarelo */
  if (status === 'in_progress' && !isPaused) {
    return (
      <div className="flex items-center justify-center gap-1.5">
        <span className="text-[11px] font-mono text-foreground">
          {formatTaskTimer(activeSeconds)}
        </span>
        {canChangeStatus && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
            onClick={handlePause}
            disabled={loading}
            title="Pausar tarefa"
          >
            <Pause className="h-3.5 w-3.5" fill="currentColor" />
          </Button>
        )}
      </div>
    );
  }

  /** Status paused: timer congelado + badge PAUSADO + botão play verde (não aplica para cancelled) */
  if ((status === 'paused' || isPaused) && status !== 'cancelled') {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-mono text-amber-600 dark:text-amber-400">
            {formatTaskTimer(activeSeconds)}
          </span>
          {canChangeStatus && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900/30"
              onClick={handleResume}
              disabled={loading}
              title="Retomar tarefa"
            >
              <Play className="h-3.5 w-3.5" fill="currentColor" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  /** Status completed/cancelled: timer final congelado, sem botões.
   *  v1.3.0: Sem started_at, exibe "00:00" (tarefa nunca foi iniciada). */
  return (
    <div className="flex items-center justify-center">
      <span className="text-[11px] font-mono text-muted-foreground">
        {startedAt ? formatTaskTimer(activeSeconds) : '00:00'}
      </span>
    </div>
  );
}
